import React, {
  useState,
  useEffect,
  useRef,
  useContext
} from "react";
import "./questionnaireQuestionForm.scss";
import { Collapse, ConfigProvider, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";

// context
import { QuestionnaireContext } from "../../contexts/questionnaireContext";

// icon
import { questionMarkIcon } from "../../constants/icons";

// component
import Loaders from "../UI/Loaders/Loaders";

// utils
import monitorInputChange from "../../utils/monitorInputChange";

type NotificationType = "success" | "info" | "warning" | "error";

const QuestionnaireQuestionForm = () => {
  const navigate = useNavigate();
  const {
    evalScope,
    sections,
    handleResponseSelect,
    questionLoading,
    handleSaveEvaluationResponse,
    handleResponseTextInputChange,
    errorParams
  } = useContext(QuestionnaireContext);
  const [api, contextHolder] = notification.useNotification();
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const opNotification = (
    type: NotificationType,
    message: string,
    descripton: string
  ) => {
    api[type]({
      message: message,
      description: descripton,
    });
  };


  const handleFormSubmit = async () => {
    try {
      setLoading(true);
      const res = await handleSaveEvaluationResponse();
      if (res.success === true) {
        navigate("/dashboard/evaluation");
      }
      if(res.errors && res.errors.length > 0){
        opNotification(
          "warning",
          "Missing Required Input",
          "Please fill all the inputs marked with *"
        );
      }
      
    } catch (error) {
      console.log("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (errorParams.length > 0) {
      errorParams.forEach((err) => {
        let ele = document.getElementById(err.path);
        if (ele) {
          ele.style.borderColor = "red";
        }
      });
    }
  }, [errorParams]);

  return (
    <div className="questionnaireQuestionForm">
      {contextHolder}
      {questionLoading ? (
        <Loaders />
      ) : (
        <>
          <div className="formContainer" ref={parentRef}>
            {sections.map((section, sectionIndex) => (
              <div
                className="sectionContainer"
                key={section._id}
                id={section._id}
                style={{
                  backgroundColor:
                    evalScope.evaluation_type === "user-driven-approach"
                      ? section.bg_color
                      : "transparent",
                }}
              >
                <div className="sectionHolder">
                  <ConfigProvider
                    theme={{
                      components: {
                        Collapse: {
                          headerBg: `${section.color}`,
                        },
                      },
                      token: {
                        colorTextHeading: "white",
                        borderRadius: 0,
                      },
                    }}
                  >
                    <Collapse
                      items={[
                        {
                          key: section._id,
                          label: section.section,
                          children: <p>{section.description}</p>,
                        },
                      ]}
                    />
                  </ConfigProvider>
                  {section.sub_sections.length > 0 ? (
                    <>
                      {section.sub_sections.map((subsec, subsecIndex) => (
                        <div className="questionComponent" key={subsecIndex}>
                          <div className="subsectionTitle">
                            <hr />
                            <span>{subsec.title}</span>
                            <hr />
                          </div>
                          {subsec.questions.map((question, questionIndex) => (
                            <div
                              className="questionHolder"
                              style={{
                                borderLeft: `2px solid ${section.color}`,
                              }}
                              key={questionIndex}
                            >
                              <div className="questionNumber">
                                <span>Q{questionIndex + 1}</span>
                              </div>
                              <div className="question">
                                <span>{question.question}</span>
                              </div>
                              <div className="responseOptions">
                                <div
                                  className="responseBtn"
                                  style={
                                    question.response === ""
                                      ? ({
                                          border: `1px solid ${section.question_color}`,
                                          "--static-color": `${section.bg_color}`,
                                          "--hover-color": `${section.question_color}`,
                                        } as React.CSSProperties)
                                      : question.response === "yes"
                                      ? ({
                                          border: `1px solid ${section.color}`,
                                          background: "white",
                                        } as React.CSSProperties)
                                      : ({
                                          border: `1px solid ${section.question_color}`,
                                          "--static-color": `${section.bg_color}`,
                                          "--hover-color": `${section.question_color}`,
                                        } as React.CSSProperties)
                                  }
                                  onClick={() => {
                                    handleResponseSelect(
                                      section._id,
                                      subsec.sub_section_id,
                                      question.question_id,
                                      "yes"
                                    );
                                  }}
                                >
                                  Yes
                                </div>
                                <div
                                  className="responseBtn"
                                  style={
                                    question.response === ""
                                      ? ({
                                          border: `1px solid ${section.question_color}`,
                                          "--static-color": `${section.bg_color}`,
                                          "--hover-color": `${section.question_color}`,
                                        } as React.CSSProperties)
                                      : question.response === "no"
                                      ? ({
                                          border: `1px solid ${section.color}`,
                                          background: "white",
                                        } as React.CSSProperties)
                                      : ({
                                          border: `1px solid ${section.question_color}`,
                                          "--static-color": `${section.bg_color}`,
                                          "--hover-color": `${section.question_color}`,
                                        } as React.CSSProperties)
                                  }
                                  onClick={() => {
                                    handleResponseSelect(
                                      section._id,
                                      subsec.sub_section_id,
                                      question.question_id,
                                      "no"
                                    );
                                  }}
                                >
                                  No
                                </div>
                                <div
                                  className="responseBtn"
                                  style={
                                    question.response === ""
                                      ? ({
                                          border: `1px solid ${section.question_color}`,
                                          "--static-color": `${section.bg_color}`,
                                          "--hover-color": `${section.question_color}`,
                                        } as React.CSSProperties)
                                      : question.response === "na"
                                      ? ({
                                          border: `1px solid ${section.color}`,
                                          background: "white",
                                        } as React.CSSProperties)
                                      : ({
                                          border: `1px solid ${section.question_color}`,
                                          "--static-color": `${section.bg_color}`,
                                          "--hover-color": `${section.question_color}`,
                                        } as React.CSSProperties)
                                  }
                                  onClick={() => {
                                    handleResponseSelect(
                                      section._id,
                                      subsec.sub_section_id,
                                      question.question_id,
                                      "na"
                                    );
                                  }}
                                >
                                  N/A
                                </div>
                                <div
                                  className="responseBtn"
                                  style={
                                    question.response === ""
                                      ? ({
                                          border: `1px solid ${section.question_color}`,
                                          "--static-color": `${section.bg_color}`,
                                          "--hover-color": `${section.question_color}`,
                                        } as React.CSSProperties)
                                      : question.response === "tbd"
                                      ? ({
                                          border: `1px solid ${section.color}`,
                                          background: "white",
                                        } as React.CSSProperties)
                                      : ({
                                          border: `1px solid ${section.question_color}`,
                                          "--static-color": `${section.bg_color}`,
                                          "--hover-color": `${section.question_color}`,
                                        } as React.CSSProperties)
                                  }
                                  onClick={() => {
                                    handleResponseSelect(
                                      section._id,
                                      subsec.sub_section_id,
                                      question.question_id,
                                      "tbd"
                                    );
                                  }}
                                >
                                  TBD
                                </div>
                              </div>

                              {question.response !== "" && (
                                <div className="inputHolder">
                                  <span>
                                    <span className="highlight">*</span> Provide
                                    a justification for your answer{" "}
                                    <Tooltip
                                      title={`Example: ${question.example_justification}`}
                                    >
                                      <span className="tipIcon">
                                        {questionMarkIcon}
                                      </span>
                                    </Tooltip>
                                    :
                                  </span>
                                  <textarea
                                    placeholder="/"
                                    id={`subsec-jus-${section._id}-${subsecIndex}-${questionIndex}`}
                                    value={question.justification}
                                    onChange={(e) => {
                                      handleResponseTextInputChange(
                                        section._id,
                                        subsec.sub_section_id,
                                        question.question_id,
                                        "justification",
                                        e.target.value
                                      );
                                      monitorInputChange(
                                        `subsec-jus-${section._id}-${subsecIndex}-${questionIndex}`
                                      );
                                    }}
                                  />
                                </div>
                              )}

                              {(question.response === "no" ||
                                question.response === "tbd") && (
                                <div className="inputHolder">
                                  <span>
                                    <span className="highlight">*</span> Specify
                                    actions planned to align to the requirement
                                    <Tooltip title="">
                                      <span className="tipIcon">
                                        {questionMarkIcon}
                                      </span>
                                    </Tooltip>
                                    :
                                  </span>
                                  <textarea
                                    placeholder="/"
                                    id={`subsec-action-${section._id}-${subsecIndex}-${questionIndex}`}
                                    value={question.actions_planned}
                                    onChange={(e) => {
                                      handleResponseTextInputChange(
                                        section._id,
                                        subsec.sub_section_id,
                                        question.question_id,
                                        "actions",
                                        e.target.value
                                      );
                                      monitorInputChange(
                                        `subsec-action-${section._id}-${subsecIndex}-${questionIndex}`
                                      );
                                    }}
                                  />
                                </div>
                              )}
                              {question.response !== "" && (
                                <div className="inputHolder">
                                  <span>Add any additional note:</span>
                                  <input
                                    type="text"
                                    placeholder="/"
                                    id={`subsec-note-${section._id}-${subsecIndex}-${questionIndex}`}
                                    value={question.note}
                                    onChange={(e) => {
                                      handleResponseTextInputChange(
                                        section._id,
                                        subsec.sub_section_id,
                                        question.question_id,
                                        "note",
                                        e.target.value
                                      );
                                      monitorInputChange(
                                        `subsec-note-${section._id}-${subsecIndex}-${questionIndex}`
                                      );
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="questionComponent">
                      {section.questions.map((question, questionIndex) => (
                        <div
                          className="questionHolder"
                          style={{
                            borderLeft: `2px solid ${section.color}`,
                          }}
                          key={questionIndex}
                        >
                          <div className="questionNumber">
                            <span>Q{questionIndex + 1}</span>
                          </div>
                          <div className="question">
                            <span>{question.question}</span>
                          </div>
                          <div className="responseOptions">
                            <div
                              className="responseBtn"
                              style={
                                question.response === ""
                                  ? ({
                                      border: `1px solid ${section.question_color}`,
                                      "--static-color": `${section.bg_color}`,
                                      "--hover-color": `${section.question_color}`,
                                    } as React.CSSProperties)
                                  : question.response === "yes"
                                  ? ({
                                      border: `1px solid ${section.color}`,
                                      background: "white",
                                    } as React.CSSProperties)
                                  : ({
                                      border: `1px solid ${section.question_color}`,
                                      "--static-color": `${section.bg_color}`,
                                      "--hover-color": `${section.question_color}`,
                                    } as React.CSSProperties)
                              }
                              onClick={() => {
                                handleResponseSelect(
                                  section._id,
                                  null,
                                  question.question_id,
                                  "yes"
                                );
                              }}
                            >
                              Yes
                            </div>
                            <div
                              className="responseBtn"
                              style={
                                question.response === ""
                                  ? ({
                                      border: `1px solid ${section.question_color}`,
                                      "--static-color": `${section.bg_color}`,
                                      "--hover-color": `${section.question_color}`,
                                    } as React.CSSProperties)
                                  : question.response === "no"
                                  ? ({
                                      border: `1px solid ${section.color}`,
                                      background: "white",
                                    } as React.CSSProperties)
                                  : ({
                                      border: `1px solid ${section.question_color}`,
                                      "--static-color": `${section.bg_color}`,
                                      "--hover-color": `${section.question_color}`,
                                    } as React.CSSProperties)
                              }
                              onClick={() => {
                                handleResponseSelect(
                                  section._id,
                                  null,
                                  question.question_id,
                                  "no"
                                );
                              }}
                            >
                              No
                            </div>
                            <div
                              className="responseBtn"
                              style={
                                question.response === ""
                                  ? ({
                                      border: `1px solid ${section.question_color}`,
                                      "--static-color": `${section.bg_color}`,
                                      "--hover-color": `${section.question_color}`,
                                    } as React.CSSProperties)
                                  : question.response === "na"
                                  ? ({
                                      border: `1px solid ${section.color}`,
                                      background: "white",
                                    } as React.CSSProperties)
                                  : ({
                                      border: `1px solid ${section.question_color}`,
                                      "--static-color": `${section.bg_color}`,
                                      "--hover-color": `${section.question_color}`,
                                    } as React.CSSProperties)
                              }
                              onClick={() => {
                                handleResponseSelect(
                                  section._id,
                                  null,
                                  question.question_id,
                                  "na"
                                );
                              }}
                            >
                              N/A
                            </div>
                            <div
                              className="responseBtn"
                              style={
                                question.response === ""
                                  ? ({
                                      border: `1px solid ${section.question_color}`,
                                      "--static-color": `${section.bg_color}`,
                                      "--hover-color": `${section.question_color}`,
                                    } as React.CSSProperties)
                                  : question.response === "tbd"
                                  ? ({
                                      border: `1px solid ${section.color}`,
                                      background: "white",
                                    } as React.CSSProperties)
                                  : ({
                                      border: `1px solid ${section.question_color}`,
                                      "--static-color": `${section.bg_color}`,
                                      "--hover-color": `${section.question_color}`,
                                    } as React.CSSProperties)
                              }
                              onClick={() => {
                                handleResponseSelect(
                                  section._id,
                                  null,
                                  question.question_id,
                                  "tbd"
                                );
                              }}
                            >
                              TBD
                            </div>
                          </div>
                          {question.response !== "" && (
                            <div className="inputHolder">
                              <span>
                                <span className="highlight">*</span> Provide a
                                justification for your answer
                                <Tooltip
                                  title={`Example: ${question.example_justification}`}
                                >
                                  <span className="tipIcon">
                                    {questionMarkIcon}
                                  </span>
                                </Tooltip>
                                :
                              </span>
                              <textarea
                                placeholder="/"
                                id={`sec-jus-${section._id}-${questionIndex}`}
                                value={question.justification}
                                onChange={(e) => {
                                  handleResponseTextInputChange(
                                    section._id,
                                    null,
                                    question.question_id,
                                    "justification",
                                    e.target.value
                                  );
                                  monitorInputChange(
                                    `sec-jus-${section._id}-${questionIndex}`
                                  );
                                }}
                              />
                            </div>
                          )}

                          {(question.response === "no" ||
                            question.response === "tbd") && (
                            <div className="inputHolder">
                              <span>
                                <span className="highlight">*</span> Specify
                                actions planned to align to the requirement
                                <Tooltip title="">
                                  <span className="tipIcon">
                                    {questionMarkIcon}
                                  </span>
                                  :
                                </Tooltip>
                              </span>
                              <textarea
                                placeholder="/"
                                id={`sec-action-${section._id}-${questionIndex}`}
                                value={question.actions_planned}
                                onChange={(e) => {
                                  handleResponseTextInputChange(
                                    section._id,
                                    null,
                                    question.question_id,
                                    "actions",
                                    e.target.value
                                  );
                                  monitorInputChange(
                                    `sec-action-${questionIndex}`
                                  );
                                }}
                              />
                            </div>
                          )}
                          {question.response !== "" && (
                            <div className="inputHolder">
                              <span>Add any additional note:</span>
                              <input
                                type="text"
                                placeholder="/"
                                id={`sec-note-${section._id}-${questionIndex}`}
                                value={question.note}
                                onChange={(e) => {
                                  handleResponseTextInputChange(
                                    section._id,
                                    null,
                                    question.question_id,
                                    "note",
                                    e.target.value
                                  );
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="formFooter">
            <div className="footerContent">
              <button
                disabled={loading ? true : false}
                onClick={handleFormSubmit}
              >
                {loading ? "Saving..." : "Save and exit"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuestionnaireQuestionForm;
